import { Type, type Static } from "@sinclair/typebox";
import { TEMPORARY_FILE_MAX_BYTES, TemporaryFileSchema } from "./document-platform.ts";
import { UuidSchema } from "./identifiers.ts";
import {
  AgentInvocationAuthorizationRequestSchema,
  AgentWorkingHomeReferencesSchema
} from "./agent-host.ts";

export const AGENT_FILE_SCHEMA_VERSION = "1.0" as const;
export const AGENT_FILE_LIMITS = Object.freeze({
  maxNamespaceEntries: 512,
  maxDirectoryEntries: 512,
  maxDirectoryCandidates: 1_024,
  maxLoadedEntries: 2_048,
  maxPathChars: 1_024,
  maxFileBytes: TEMPORARY_FILE_MAX_BYTES,
  maxOutputFiles: 16,
  maxOutputBytes: TEMPORARY_FILE_MAX_BYTES
});

const TimestampSchema = Type.String({ format: "date-time" });
const ChecksumSchema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const VirtualPathSchema = Type.String({
  minLength: 1,
  maxLength: AGENT_FILE_LIMITS.maxPathChars,
  pattern: "^/[^\\\\\\u0000-\\u001f\\u007f]*$"
});
const OpenCloudRelativePathSchema = Type.String({
  maxLength: AGENT_FILE_LIMITS.maxPathChars,
  pattern: "^(?:$|[^/\\\\\\u0000-\\u001f\\u007f]+(?:/[^/\\\\\\u0000-\\u001f\\u007f]+)*)$"
});

export const AgentDocumentFileSourceSchema = Type.Object(
  {
    kind: Type.Literal("document"),
    documentId: UuidSchema,
    representation: Type.Union([Type.Literal("source"), Type.Literal("canonical_markdown")]),
    version: Type.String({ minLength: 1, maxLength: 256 })
  },
  { additionalProperties: false }
);

export const AgentTemporaryFileSourceSchema = Type.Object(
  {
    kind: Type.Literal("temporary_file"),
    temporaryFileId: UuidSchema,
    contentSha256: ChecksumSchema,
    expiresAt: TimestampSchema
  },
  { additionalProperties: false }
);

export const AgentAppDirectoryFileSourceSchema = Type.Object(
  {
    kind: Type.Literal("app_directory_file"),
    appKey: Type.Literal("browser-expert"),
    resourceId: Type.String({ minLength: 1, maxLength: 1_024 }),
    version: Type.String({ minLength: 1, maxLength: 256 }),
    contentSha256: ChecksumSchema
  },
  { additionalProperties: false }
);

export const AgentFileSourceSchema = Type.Union([
  AgentDocumentFileSourceSchema,
  AgentTemporaryFileSourceSchema,
  AgentAppDirectoryFileSourceSchema
]);

export const AgentFileSpaceDirectorySourceSchema = Type.Object(
  {
    kind: Type.Literal("file_space_directory"),
    fileSpaceId: UuidSchema,
    openCloudResourceId: Type.String({ minLength: 1, maxLength: 1_024 }),
    path: OpenCloudRelativePathSchema
  },
  { additionalProperties: false }
);

export const AgentDocumentDirectorySourceSchema = Type.Object(
  {
    kind: Type.Literal("document_directory"),
    documentId: UuidSchema
  },
  { additionalProperties: false }
);

export const AgentFileDirectorySourceSchema = Type.Union([
  AgentFileSpaceDirectorySourceSchema,
  AgentDocumentDirectorySourceSchema
]);

const AgentFileDirectoryEntrySchema = Type.Object(
  {
    path: VirtualPathSchema,
    kind: Type.Literal("directory"),
    mtime: TimestampSchema,
    source: Type.Optional(AgentFileDirectorySourceSchema)
  },
  { additionalProperties: false }
);

const AgentFileRegularEntrySchema = Type.Object(
  {
    path: VirtualPathSchema,
    kind: Type.Literal("file"),
    sizeBytes: Type.Integer({ minimum: 0, maximum: AGENT_FILE_LIMITS.maxFileBytes }),
    mediaType: Type.Union([Type.String({ minLength: 1, maxLength: 256 }), Type.Null()]),
    mtime: TimestampSchema,
    source: AgentFileSourceSchema
  },
  { additionalProperties: false }
);

export const AgentFileEntrySchema = Type.Union([
  AgentFileDirectoryEntrySchema,
  AgentFileRegularEntrySchema
]);

export const AgentFileNamespaceSchema = Type.Object(
  {
    schemaVersion: Type.Literal(AGENT_FILE_SCHEMA_VERSION),
    entries: Type.Array(AgentFileEntrySchema, {
      maxItems: AGENT_FILE_LIMITS.maxNamespaceEntries
    })
  },
  { additionalProperties: false }
);

export const AgentFileNamespaceResolveRequestSchema = Type.Object(
  {
    invocation: AgentInvocationAuthorizationRequestSchema,
    workingHomeFiles: Type.Optional(AgentWorkingHomeReferencesSchema)
  },
  { additionalProperties: false }
);

export const AgentWorkingFileFindRequestSchema = Type.Object(
  {
    invocation: AgentInvocationAuthorizationRequestSchema,
    fileName: Type.String({
      minLength: 1,
      maxLength: 512,
      pattern: "^(?!\\.{1,2}$)[^/\\\\\\u0000-\\u001f\\u007f]+$"
    })
  },
  { additionalProperties: false }
);

export const AgentWorkingFileFindResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(AGENT_FILE_SCHEMA_VERSION),
    file: Type.Union([TemporaryFileSchema, Type.Null()])
  },
  { additionalProperties: false }
);

export const AgentFileDirectoryListRequestSchema = Type.Object(
  {
    invocation: AgentInvocationAuthorizationRequestSchema,
    directory: Type.Object(
      {
        path: VirtualPathSchema,
        source: AgentFileDirectorySourceSchema
      },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);

export const AgentFileDirectoryListResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(AGENT_FILE_SCHEMA_VERSION),
    entries: Type.Array(AgentFileEntrySchema, {
      maxItems: AGENT_FILE_LIMITS.maxDirectoryEntries
    })
  },
  { additionalProperties: false }
);

export const AgentFileReadRequestSchema = Type.Object(
  {
    invocation: AgentInvocationAuthorizationRequestSchema,
    source: AgentFileSourceSchema
  },
  { additionalProperties: false }
);

export const AgentFileReadResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(AGENT_FILE_SCHEMA_VERSION),
    contentBase64: Type.String({
      maxLength: Math.ceil((AGENT_FILE_LIMITS.maxFileBytes * 4) / 3) + 4
    }),
    sizeBytes: Type.Integer({ minimum: 0, maximum: AGENT_FILE_LIMITS.maxFileBytes }),
    version: Type.String({ minLength: 1, maxLength: 256 })
  },
  { additionalProperties: false }
);

export const AGENT_OUTPUT_MEDIA_TYPES = [
  "application/json",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "image/jpeg",
  "image/png",
  "text/csv",
  "text/html",
  "text/markdown",
  "text/plain",
  "text/typescript"
] as const;

export const AgentOutputMediaTypeSchema = Type.Union([
  Type.Literal("application/json"),
  Type.Literal("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"),
  Type.Literal("application/vnd.openxmlformats-officedocument.presentationml.presentation"),
  Type.Literal("image/jpeg"),
  Type.Literal("image/png"),
  Type.Literal("text/csv"),
  Type.Literal("text/html"),
  Type.Literal("text/markdown"),
  Type.Literal("text/plain"),
  Type.Literal("text/typescript")
]);

export const AgentOutputPublishRequestSchema = Type.Object(
  {
    invocation: AgentInvocationAuthorizationRequestSchema,
    idempotencyKey: Type.String({
      minLength: 1,
      maxLength: 128,
      pattern: "^[A-Za-z0-9:_-]+$"
    }),
    output: Type.Object(
      {
        fileName: Type.String({
          minLength: 1,
          maxLength: 512,
          pattern: "^(?!\\.{1,2}$)[^/\\\\\\u0000-\\u001f\\u007f]+$"
        }),
        mediaType: AgentOutputMediaTypeSchema,
        sizeBytes: Type.Integer({ minimum: 1, maximum: AGENT_FILE_LIMITS.maxFileBytes }),
        contentSha256: ChecksumSchema,
        contentBase64: Type.String({
          minLength: 4,
          maxLength: Math.ceil((AGENT_FILE_LIMITS.maxFileBytes * 4) / 3) + 4
        })
      },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);

export const AgentOutputPublishResultSchema = TemporaryFileSchema;

export type AgentFileSourceV1 = Static<typeof AgentFileSourceSchema>;
export type AgentFileDirectorySourceV1 = Static<typeof AgentFileDirectorySourceSchema>;
export type AgentFileEntryV1 = Static<typeof AgentFileEntrySchema>;
export type AgentFileNamespaceV1 = Static<typeof AgentFileNamespaceSchema>;
export type AgentFileNamespaceResolveRequestV1 = Static<
  typeof AgentFileNamespaceResolveRequestSchema
>;
export type AgentWorkingFileFindRequestV1 = Static<typeof AgentWorkingFileFindRequestSchema>;
export type AgentWorkingFileFindResultV1 = Static<typeof AgentWorkingFileFindResultSchema>;
export type AgentFileDirectoryListRequestV1 = Static<typeof AgentFileDirectoryListRequestSchema>;
export type AgentFileDirectoryListResultV1 = Static<typeof AgentFileDirectoryListResultSchema>;
export type AgentFileReadRequestV1 = Static<typeof AgentFileReadRequestSchema>;
export type AgentFileReadResultV1 = Static<typeof AgentFileReadResultSchema>;
export type AgentOutputMediaTypeV1 = Static<typeof AgentOutputMediaTypeSchema>;
export type AgentOutputPublishRequestV1 = Static<typeof AgentOutputPublishRequestSchema>;
export type AgentOutputPublishResultV1 = Static<typeof AgentOutputPublishResultSchema>;
